import type { NativeStackHeaderProps } from '@react-navigation/native-stack';
import { Link, router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { ComponentPropsWithoutRef, type ComponentType, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Platform, StyleSheet, type View } from 'react-native';

import {
  HeaderFrame,
  HeaderIconButton,
  HeaderTitleText,
  IconChevronLeft,
  IconClose,
  Text,
  YStack,
  tokens,
} from '@fhs/ui';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { orderTypeFocusTargetProps } from '../../service-mode-dialog/order-type-focus-handoff';
const HEADER_SIDE_SLOT_WIDTH = 56;

type GetScreenTitleHeaderOptions = {
  backStyle?: 'back' | 'close' | 'dynamic' | 'none';
  closeBehavior?: 'dismissTo' | 'tabHistory';
  testID?: string;
  cannotGoBackHref?: ComponentPropsWithoutRef<typeof Link>['href'];
  shouldBackToRoot?: boolean;
  showDivider?: boolean;
  subtitle?: string;
  /** Web: move keyboard/screen reader focus to the header back/close control when this screen is shown (e.g. delivery modal). */
  focusCloseButtonOnWebFocus?: boolean;
  middleComponent?: ComponentType;
};

type ScreenTitleHeaderProps = NativeStackHeaderProps & {
  headerOpts?: GetScreenTitleHeaderOptions;
};

function ScreenTitleHeader({ options, navigation, headerOpts }: ScreenTitleHeaderProps) {
  const { formatMessage } = useIntl();
  const {
    backStyle = 'dynamic',
    closeBehavior = 'dismissTo',
    cannotGoBackHref = '/',
    testID,
    shouldBackToRoot = false,
    showDivider = false,
    subtitle,
    focusCloseButtonOnWebFocus = false,
    middleComponent: MiddleComponent,
  } = headerOpts ?? {};
  const headerControlRef = useRef<View>(null);

  useFocusEffect(
    useCallback(() => {
      if (!focusCloseButtonOnWebFocus || Platform.OS !== 'web') {
        return;
      }
      const id = requestAnimationFrame(() => {
        const el = headerControlRef.current as unknown as { focus?: () => void } | null;
        el?.focus?.();
      });
      return () => cancelAnimationFrame(id);
    }, [focusCloseButtonOnWebFocus])
  );
  const canGoBack = navigation.canGoBack();
  const { isFromCart } = useLocalSearchParams<{ isFromCart: string }>();
  const href = canGoBack && !shouldBackToRoot ? '../' : cannotGoBackHref;
  const showsBackChevron = (backStyle === 'dynamic' && canGoBack) || backStyle === 'back';
  const headerControlLabel = showsBackChevron
    ? formatMessage({ id: 'back' })
    : formatMessage({ id: 'close' });
  let headerControlHint: string | undefined;
  if (Platform.OS !== 'web') {
    headerControlHint = showsBackChevron
      ? isFromCart
        ? formatMessage({ id: 'headerBackToCartHint' })
        : formatMessage({ id: 'headerBackHint' })
      : formatMessage({ id: 'headerCloseHint' });
  }
  const icon =
    (backStyle === 'dynamic' && canGoBack) || backStyle === 'back' ? (
      <IconChevronLeft
        accessible={false}
        accessibilityElementsHidden
        importantForAccessibility="no"
        testID={testID}
      />
    ) : (
      <IconClose
        accessible={false}
        accessibilityElementsHidden
        importantForAccessibility="no"
        testID={testID}
      />
    );

  const closeToPreviousTab = () => {
    const tabNavigation = navigation.getParent();

    if (!tabNavigation) {
      router.replace(cannotGoBackHref);
      return;
    }

    const tabState = tabNavigation.getState();
    const hasPreviousTab =
      'history' in tabState && Array.isArray(tabState.history) && tabState.history.length > 1;

    if (hasPreviousTab) {
      tabNavigation.goBack();
    } else {
      tabNavigation.navigate('index');
    }
  };

  const handleHeaderControlPress = () => {
    if (isFromCart) {
      router.dismissTo(routes.cart);
      return;
    }

    if (closeBehavior === 'tabHistory' && !showsBackChevron) {
      closeToPreviousTab();
      return;
    }

    router.dismissTo(href);
  };

  return (
    <HeaderFrame
      style={{
        borderBottomWidth: showDivider ? 1 : 0,
        borderColor: tokens.colors.$blackOpacity10,
      }}
    >
      <HeaderFrame.Left style={styles.sideSlot}>
        {backStyle === 'none' ? null : options?.title === 'Support' ? (
          <HeaderIconButton
            ref={headerControlRef}
            size="lg"
            style={styles.headerControlButton}
            {...orderTypeFocusTargetProps}
            accessibilityLabel={headerControlLabel}
            accessibilityHint={headerControlHint}
            aria-label={headerControlLabel}
            onPress={async () => {
              // checking for web before checking for window
              const isSupportPageWebView =
                Platform.OS === 'web' &&
                window &&
                window?.Pypestream &&
                window?.Pypestream?.shutdown;
              if (isSupportPageWebView) {
                // needs time to shutdown before navigation to allow cleanup of the pypestream session
                window.Pypestream?.shutdown();
                await new Promise(resolve => setTimeout(resolve, 500));
              }

              router.dismissTo(href);
            }}
          >
            {icon}
          </HeaderIconButton>
        ) : (
          <HeaderIconButton
            ref={headerControlRef}
            size="lg"
            style={styles.headerControlButton}
            {...orderTypeFocusTargetProps}
            accessibilityLabel={headerControlLabel}
            accessibilityHint={headerControlHint}
            aria-label={headerControlLabel}
            onPress={handleHeaderControlPress}
          >
            {icon}
          </HeaderIconButton>
        )}
      </HeaderFrame.Left>
      <HeaderFrame.Middle layout="inline">
        {MiddleComponent ? (
          <MiddleComponent />
        ) : (
          <YStack style={styles.titleStack}>
            <HeaderTitleText text={options.title ?? ''} />
            {subtitle ? <Text.Paragraph size="sm">{subtitle}</Text.Paragraph> : null}
          </YStack>
        )}
      </HeaderFrame.Middle>
      <HeaderFrame.Right style={styles.sideSlot} />
    </HeaderFrame>
  );
}

export const getScreenTitleHeader = (getScreenTitleHeaderOpts?: GetScreenTitleHeaderOptions) => {
  return (props: NativeStackHeaderProps) => (
    <ScreenTitleHeader {...props} headerOpts={getScreenTitleHeaderOpts} />
  );
};

const styles = StyleSheet.create({
  sideSlot: {
    width: HEADER_SIDE_SLOT_WIDTH,
    minWidth: HEADER_SIDE_SLOT_WIDTH,
    flexGrow: 0,
    flexShrink: 0,
    paddingRight: 0,
  },
  titleStack: {
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
  },
  headerControlButton: {
    marginHorizontal: 0,
  },
});
